<template>
  <div class="bg-marketing-light pb-5">
    <!-- Breadcrumb Section -->
    <section class="bg-white py-3" style="border-bottom: 1px solid #E2E8F0;">
      <CContainer>
        <div class="d-flex align-items-center small text-muted">
          <router-link to="/" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Home</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <router-link to="/calculators" class="text-marketing-gray-600 font-weight-500" style="text-decoration: none;">Calculators</router-link>
          <CIcon name="cil-chevron-right" size="sm" class="mx-2"/>
          <span class="text-marketing-dark font-weight-bold">SST Calculator</span>
        </div>
      </CContainer>
    </section>

    <!-- Hero Section -->
    <section class="py-5 bg-white">
      <CContainer>
        <CRow class="justify-content-center">
          <CCol lg="8" class="text-center">
            <div class="marketing-icon-wrapper mx-auto mb-4" style="width: 80px; height: 80px; font-size: 2rem;">
              <CIcon name="cil-calculator" size="custom-size" style="width: 40px; height: 40px;" class="text-warning"/>
            </div>
            <h1 class="display-4 font-weight-bold marketing-heading mb-3">
              SST Calculator Malaysia
            </h1>
            <p class="lead marketing-body mb-0">
              Easily calculate inclusive and exclusive Sales and Services Tax (SST) at 6%, 8% or 10%.
            </p>
          </CCol>
        </CRow>
      </CContainer>
    </section>

    <!-- Main Calculator Section -->
    <section class="py-5">
      <CContainer>
        <CRow>
          <!-- Left Column: Interactive Calculator -->
          <CCol lg="8" class="mb-4">
            <CCard class="border-0 shadow-sm h-100">
              <CCardBody class="p-4 p-md-5">
                <h2 class="h3 font-weight-bold text-marketing-dark mb-4">Calculate SST Amount</h2>

                <!-- Input: Amount -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark">Item Amount (RM)</label>
                  <div class="input-group input-group-lg">
                    <div class="input-group-prepend">
                      <span class="input-group-text bg-white border-right-0 text-muted font-weight-bold">RM</span>
                    </div>
                    <input 
                      type="number" 
                      class="form-control border-left-0 pl-0" 
                      v-model.number="amount"
                      @input="calculate"
                      placeholder="100"
                      min="0"
                      step="0.01"
                      style="box-shadow: none;"
                    >
                  </div>
                </div>

                <!-- Input: Tax Scope/Mode -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark mb-3">Tax Mode</label>
                  <CRow>
                    <CCol md="6" class="mb-2">
                       <CButton 
                         block
                         :color="calculationMode === 'exclusive' ? 'warning' : 'light'"
                         class="py-3 font-weight-bold h-100 text-left px-4"
                         style="border-width: 2px;"
                         :class="{'border-warning': calculationMode === 'exclusive', 'text-white': calculationMode === 'exclusive'}"
                         @click="selectMode('exclusive')"
                       >
                         <h5 class="h6 font-weight-bold mb-1" :class="calculationMode === 'exclusive' ? 'text-white' : 'text-marketing-dark'">Exclusive (Add Tax)</h5>
                         <span class="small font-weight-normal" :class="calculationMode === 'exclusive' ? 'text-white-50' : 'text-muted'">Amount is purely cost. Add SST on top.</span>
                       </CButton>
                    </CCol>
                    <CCol md="6" class="mb-2">
                       <CButton 
                         block
                         :color="calculationMode === 'inclusive' ? 'warning' : 'light'"
                         class="py-3 font-weight-bold h-100 text-left px-4"
                         style="border-width: 2px;"
                         :class="{'border-warning': calculationMode === 'inclusive', 'text-white': calculationMode === 'inclusive'}"
                         @click="selectMode('inclusive')"
                       >
                         <h5 class="h6 font-weight-bold mb-1" :class="calculationMode === 'inclusive' ? 'text-white' : 'text-marketing-dark'">Inclusive (Extract Tax)</h5>
                         <span class="small font-weight-normal" :class="calculationMode === 'inclusive' ? 'text-white-50' : 'text-muted'">Amount includes SST. Find the base cost.</span>
                       </CButton>
                    </CCol>
                  </CRow>
                </div>

                <!-- Input: Tax Rate -->
                <div class="form-group mb-4">
                  <label class="font-weight-600 text-marketing-dark mb-3">SST Rate</label>
                  <CRow>
                    <CCol md="4" class="mb-2">
                       <CButton 
                         block
                         :color="taxRate === 6 ? 'warning' : 'light'"
                         class="py-3 font-weight-bold"
                         style="border-width: 2px;"
                         :class="{'border-warning': taxRate === 6, 'text-white': taxRate === 6}"
                         @click="selectRate(6)"
                       >
                         6% <br><small class="font-weight-normal text-marketing-dark opacity-75" :class="{'text-white': taxRate === 6}">F&B/Telecom</small>
                       </CButton>
                    </CCol>
                    <CCol md="4" class="mb-2">
                       <CButton 
                         block
                         :color="taxRate === 8 ? 'warning' : 'light'"
                         class="py-3 font-weight-bold"
                         style="border-width: 2px;"
                         :class="{'border-warning': taxRate === 8, 'text-white': taxRate === 8}"
                         @click="selectRate(8)"
                       >
                         8% <br><small class="font-weight-normal text-marketing-dark opacity-75" :class="{'text-white': taxRate === 8}">Standard Service</small>
                       </CButton>
                    </CCol>
                    <CCol md="4" class="mb-2">
                       <CButton 
                         block
                         :color="taxRate === 10 ? 'warning' : 'light'"
                         class="py-3 font-weight-bold"
                         style="border-width: 2px;"
                         :class="{'border-warning': taxRate === 10, 'text-white': taxRate === 10}"
                         @click="selectRate(10)"
                       >
                         10% <br><small class="font-weight-normal text-marketing-dark opacity-75" :class="{'text-white': taxRate === 10}">Sales Tax</small>
                       </CButton>
                    </CCol>
                  </CRow>
                </div>

                <!-- Results Panel (Active) -->
                <div v-if="amount > 0" class="bg-warning text-dark p-4 p-md-5 rounded-lg mt-5" style="background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);">
                  <h4 class="h5 font-weight-bold text-white mb-4 d-flex align-items-center">
                    <CIcon name="cil-check-circle" class="mr-2"/> Calculation Results breakdown
                  </h4>

                  <CRow>
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded h-100 bg-white shadow-sm border-0">
                        <div class="small text-muted mb-1 text-uppercase font-weight-bold letter-spacing-1">Base Amount</div>
                        <div class="h3 font-weight-bold text-marketing-dark mb-0">RM {{ baseAmount.toFixed(2) }}</div>
                        <div class="small mt-1 text-muted">Without Tax</div>
                      </div>
                    </CCol>
                    <CCol md="4" class="mb-3 mb-md-0">
                      <div class="p-3 rounded h-100 bg-white shadow-sm border-0 border-top" style="border-top-width: 3px !important; border-top-color: #F59E0B !important;">
                        <div class="small text-warning mb-1 text-uppercase font-weight-bold letter-spacing-1">SST ({{ taxRate }}%)</div>
                        <div class="h3 font-weight-bold text-warning mb-0">RM {{ sstAmount.toFixed(2) }}</div>
                        <div class="small mt-1 text-muted">Tax Amount</div>
                      </div>
                    </CCol>
                    <CCol md="4">
                      <div class="p-3 rounded h-100 shadow-sm border-0" style="background: #FFFBEB;">
                        <div class="small text-marketing-dark mb-1 text-uppercase font-weight-bold letter-spacing-1">Total Due</div>
                        <div class="h3 font-weight-bold text-marketing-dark mb-0">RM {{ totalAmount.toFixed(2) }}</div>
                        <div class="small mt-1 text-muted">Base + SST</div>
                      </div>
                    </CCol>
                  </CRow>
                </div>

                <!-- Empty State -->
                <div v-else class="mt-5 p-5 border text-center rounded-lg" style="border-style: dashed !important; border-width: 2px !important; border-color: #cbd5e1 !important; background: #f8fafc;">
                   <CIcon name="cil-calculator" size="2xl" class="text-muted mb-3 opacity-50"/>
                   <p class="text-muted font-weight-600 mb-0">Enter an amount above to see SST calculations instantly.</p>
                </div>
              </CCardBody>
            </CCard>
          </CCol>

          <!-- Right Column: Info Sidebar -->
          <CCol lg="4">
            <!-- Quick Facts Card -->
            <CCard class="border-0 shadow-sm mb-4">
              <CCardBody class="p-4">
                 <h3 class="h5 font-weight-bold text-marketing-dark mb-4">SST Quick Facts</h3>

                 <div class="mb-4">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">8% Service Tax Update</h6>
                   <p class="text-muted small mb-0">As of March 2024, Service Tax in Malaysia has been increased from 6% to <strong>8%</strong> for most taxable services.</p>
                 </div>
                 
                 <div class="mb-4 border-top pt-3">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">Exemptions to 8% Rule</h6>
                   <p class="text-muted small mb-0">Food & Beverage (F&B), Telecommunication, and Local Parking services remain at the original <strong>6%</strong> rate.</p>
                 </div>

                 <div class="mb-4 border-top pt-3">
                   <h6 class="font-weight-bold text-marketing-dark mb-1">10% Sales Tax</h6>
                   <p class="text-muted small">Standard sales tax is levied at <strong>10%</strong> on most manufactured or imported goods in Malaysia.</p>
                 </div>

                 <div class="border-top pt-4 text-center mt-5">
                   <CButton block class="btn-marketing-accent py-3 font-weight-bold mb-2" to="/signup">
                     Create SST-Ready Invoices
                   </CButton>
                   <small class="text-muted">Use Niagaku's auto-SST invoicing.</small>
                 </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SstCalculator',
  metaInfo: {
    title: 'SST Calculator Malaysia - Free Sales & Service Tax Calculator',
    meta: [
      {
         name: 'description',
         content: 'Calculate Malaysia inclusive and exclusive SST (Sales & Services Tax) at 6%, 8% and 10%. Perfect for issuing invoices.'
      }
    ]
  },
  data() {
    return {
      amount: 100,
      calculationMode: 'exclusive', // exclusive or inclusive
      taxRate: 8,
      baseAmount: 0,
      sstAmount: 0,
      totalAmount: 0
    }
  },
  mounted() {
    this.calculate()
  },
  methods: {
    selectMode(mode) {
      this.calculationMode = mode;
      this.calculate()
    },
    selectRate(rate) {
      this.taxRate = rate;
      this.calculate()
    },
    calculate() {
      if (!this.amount || this.amount <= 0) {
        this.baseAmount = 0;
        this.sstAmount = 0;
        this.totalAmount = 0;
        return;
      }

      let pct = this.taxRate / 100.0;
      let val = parseFloat(this.amount);

      if (this.calculationMode === 'exclusive') {
          // Amount is base. Add tax to it.
          this.baseAmount = val;
          this.sstAmount = val * pct;
          this.totalAmount = this.baseAmount + this.sstAmount;
      } else {
          // Amount is total with tax. Work backwards.
          this.totalAmount = val;
          this.baseAmount = val / (1 + pct);
          this.sstAmount = this.totalAmount - this.baseAmount;
      }
    }
  }
}
</script>

<style scoped>
.hover-bg-light:hover {
  background-color: #f8fafc;
}
.transition-all {
  transition: all 0.2s ease-in-out;
}
.letter-spacing-1 {
  letter-spacing: 1px;
}
.opacity-80 {
  opacity: 0.8;
}
.opacity-75 {
  opacity: 0.75;
}
.opacity-50 {
  opacity: 0.5;
}
</style>
